import React, { useState, useRef } from 'react'
import useDimensions from 'react-use-dimensions'
import { scaleLinear } from 'd3-scale'
import debounce from 'lodash/debounce'
import classnames from 'classnames'
import { group } from 'd3-array'
import Tooltip from '../WorldMap/Tooltip'
import moment from 'moment'
import './TimelapseDateSlider.scss'

let wasPaused = false
export default function TimelapseDateSlider(props) {
  const {
    dateIndex,
    dateSet,
    paused,
    setPaused,
    setDateIndex,
    dateSliderWidth,
    singleColumnMode,
    timelapseRegionKey,
    regionDataByKey,
    populations,
    casesDeathsMetricKey,
    populationKey,
    colorScale,
  } = props


  const [sliderRef, { x }] = useDimensions()
  const backgroundRef = useRef()
  const [slidingDateIndex, setSlidingIndex] = useState(null)
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const barWidth = (dateSliderWidth || 300)

  const max = dateSet.length - 1
  const paddingLeft = 30
  const paddingRight = singleColumnMode ? paddingLeft : 0
  const timeScale = scaleLinear()
      .domain([0, max])
      .range([paddingLeft, barWidth - paddingLeft - paddingRight])
      .clamp(true)


  const debouncedSetDateIndex = debounce(setDateIndex, 100)
  const moveSlide = event => {
    const mouseX = event.clientX - x
    const movedDateIndex = ~~timeScale.invert(mouseX)
    setSlidingIndex(movedDateIndex)
    debouncedSetDateIndex (movedDateIndex)
  }
  const endSlide = event => {
    document.removeEventListener('mousemove', moveSlide)
    document.removeEventListener('mouseup', endSlide)
    setSlidingIndex(null)
    setPaused(wasPaused)
  }


  const startSliding = (event) => {
    const mouseStart = event.clientX - x
    const movedDateIndex = ~~timeScale.invert(mouseStart)

    setSlidingIndex(movedDateIndex)
    debouncedSetDateIndex(movedDateIndex)
    document.addEventListener('mousemove', moveSlide)
    document.addEventListener('mouseup', endSlide)
    wasPaused = paused
    setPaused(true)
  }

  const hoverSlider = (event) => {
    const mouseStart = event.clientX - x
    const movedDateIndex = ~~timeScale.invert(mouseStart)
    const xOffset = 12
    const y = event.target.getBoundingClientRect().y
    const yOffset = 55
    setHoveredIndex({
      index: movedDateIndex,
      x: event.clientX + x + xOffset,
      y: y + yOffset
    })
    // console.log(movedDateIndex)
  }

  const hoverOffSlider = (event) => {
    setHoveredIndex(null)
  }

  const firstAndLast = [0, dateSet.length - 1]
  const verySmallSlider = dateSliderWidth < 700
  const nearThreshold = dateSliderWidth > 960 ? 10 : verySmallSlider ? 31 : 14;
  const wideSlider = dateSliderWidth > 1280;

  const tickMarks = dateSet
    .filter(d => {
      if (firstAndLast.includes(d.dateIndex)) {
        return true
      } else if (d.date.getDate() === 1 || (wideSlider && d.date.getDate() === 15)) {
        const nearFirstOrLast = firstAndLast.reduce((near, tick) => { return near || Math.abs(tick - d.dateIndex) < nearThreshold }, false)
        if (nearFirstOrLast) {
          return false
        }
        return !singleColumnMode || d.date.getMonth() % 2 === 1
      }
      return false
    })
    .map(d => d.dateIndex)
  let previousTickYear = null
  const ticks = tickMarks.map(tick => {
    const tickX = timeScale(tick)
    const date = dateSet[tick].date
    const year = date.getFullYear()
    const showFullYear = year !== previousTickYear
    previousTickYear = year

    const monthDateLabel = `${date.getMonth() + 1}/${date.getDate()}`
    const tickLabel = `${monthDateLabel}${showFullYear ? `/${year - 2000}` : ''}`
    return (
      <div className='tick' key={tick} style= {{
        transform: `translateX(${tickX}px) translateX(-50%)`
      }}>
        <div>{tickLabel}</div>
        {/* <div className='tickLine' /> */}
      </div>
    )
  })


  const dateIndexToUse = slidingDateIndex ? slidingDateIndex : dateIndex
  const dateX = timeScale(dateIndexToUse)

  let sliderRects = null
  let dotColor = null
  if (populations && regionDataByKey) {
    const timelapseSliderData = regionDataByKey[timelapseRegionKey]

    // we have US fips as two digits, but they are stored in population oboject as 5 digits 23 => 23000
    const populationFips = `${timelapseSliderData.fips}${timelapseSliderData.fips.length === 2 & timelapseSliderData.fips !== '-1' ? '000' : ''}`
    const timelapseSliderPopulation = populations[populationFips]

    const datesWithData = dateSet.map(date => {
      return {
        ...timelapseSliderData.dataMap[date.dateString],
        ...date
      }
    })
    // console.log(datesWithData)

    // monthly groups:
    // const datesGrouped = Array.from(group(datesWithData, (d, i) => `${d.date.getFullYear()}-${d.date.getMonth()}`))
    // daily groups
    const datesGrouped = Array.from(group(datesWithData, (d, i) => d.dateString))

    sliderRects = datesGrouped.map(([dateGroupId, dateGroupDates], dateGroupIndex) => {
      // const dateGroupSums = dateGroupDates.reduce(([dateString, date]) => {

      // }, { [`new_${metricKey}`] : 0, [`new_${metricKey}_14dayAverage`])

      // const data = timeSliderData.dataMap[dateGroup]
      const sumKeys = [`new_${casesDeathsMetricKey}`, `new_${casesDeathsMetricKey}_14dayAverage`]
      const initial = sumKeys.reduce((sum, key) => { return { ...sum, [`${key}`]: 0 }}, {})
      const groupSums = dateGroupDates.reduce((sums, date) => {

        const result = sumKeys.reduce((r, key) => {
          return {
            ...r,
            [`${key}`]: date[key] + r[key]
          }
        }, sums)
        return result
      }, initial)
      const average = sumKeys.reduce((sum, key) => {
        return {
          ...sum,
          [`${key}`]: groupSums[key] / dateGroupDates.length
        }
      }, {})

      const perCapita = average[`new_${casesDeathsMetricKey}_14dayAverage`] / timelapseSliderPopulation[populationKey]
      const color = colorScale(perCapita)

      let x1 = timeScale(dateGroupDates[0].dateIndex) - paddingLeft
      // below is awkward... if last item, end is barWidth, else,
      // grab next item [+ 1], grab it's groupped data points [1], and grab first item in group [0]
      let x2 = dateGroupIndex < datesGrouped.length - 1 ? timeScale(datesGrouped[dateGroupIndex + 1][1][0].dateIndex) - paddingLeft : barWidth - paddingLeft * 3
      x1 = Math.floor(x1)
      x2 = Math.ceil(x2)

      let rectWidth = (x2 - x1)
      x1 -= rectWidth / 2
      x1 = Math.round(x1)
      // rectWidth = Math.round(rectWidth)
      let visible = x1 <= (dateX - paddingLeft)
      if (visible) dotColor = color
      let opacity = visible ? 1 : 0
      return (
        <div
          key={dateGroupIndex}
          style={{
            backgroundColor: color,
            width: rectWidth,
            transform: `translate3D(${x1}px, -1.5px, 0)`,
            opacity,
          }}
        />
      )

    })
    // console.log(timelapseSliderData, timelapseSliderPopulation)
  }

  let tooltip = null
  if (hoveredIndex)  {
    const dataKey = props.dateSet[hoveredIndex.index].dateString
    if (regionDataByKey && regionDataByKey[timelapseRegionKey] && regionDataByKey[timelapseRegionKey].dataMap[dataKey]) {
      const dateLabel = moment(props.dateSet[hoveredIndex.index].date).format('M/D/YYYY')
      tooltip =  (
        <Tooltip
          hoveredCounty={hoveredIndex}
          // properties={regionProperties[hoveredFips.county.id]}
          // properties={props.regionProperties}
          usMapRef={backgroundRef}
          populationKey={populationKey}
          colorScale={colorScale}
          {...props}
          metricKey={casesDeathsMetricKey}
          dataKey={dataKey}
          hideInstructions={true}
          hideCounty={true}
          comparisonDataKey={timelapseRegionKey}
          // hidePopulation={true}
          dateLabel={dateLabel}
          backgroundColor='rgba(255, 255, 255, 0.9)'
          defaultXOffset={0}
        />
      )
    }
  }

  const dotX = hoveredIndex !== null ? timeScale(hoveredIndex.index) : dateX
  return (
    <div className='TimelapseDateSlider' ref={sliderRef}>
      <div>
        <div className='ticks'>
          {ticks}
        </div>
        <div style={{
            padding: `0 0 1em ${paddingLeft}px`
          }}
          className='sliderWrapper'
        >
          <div
            ref={backgroundRef}
            className='backgroundHandle'
            onMouseDown={startSliding}
            onMouseMove={hoverSlider}
            onMouseOut={hoverOffSlider}
              style={{
                width: barWidth - paddingLeft * 2 - paddingRight,
            }}
          >
            <div className='background'>
              <div className='backgroundFill' style={{ width: dateX - paddingLeft}} />
              {sliderRects}
            </div>
          </div>
          <div
            className={ classnames('button', { moving: slidingDateIndex !== null })}
            style={{ transform: `translate(${dateX}px, 0) translate(-50%, -1px)`, backgroundColor: dotColor }}
            onMouseDown={startSliding}
          />
          <div
            className='button dateIndicator'
            style={{ transform: `translate(${dotX}px, 0) translate(-50%, 29px)` }}
          />
          {tooltip}
          {/* <div
            className='label'
            style={{ transform: `translate(${dateX}px, 0) translateX(-50%)` }}
          >
            {dateSet[dateIndexToUse].dateString}
          </div> */}



        </div>
      </div>
    </div>
  )
}